import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Table, RangeTest, LookupTest, MatrixAxis } from '../../_models/table';
import { matrixAxisSummaryLabel, MatrixAxisSummaryContext } from '../../_models/table-matrix-axis-summary';
import { SelectorsService } from '../../_services/selectors.service';
import { CalcConfigService } from '../../_services/calc-config.service';
import { DecimalPipe } from '@angular/common';
import { MatCard, MatCardHeader, MatCardTitle, MatCardContent } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatInput, MatHint } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { InputSelectorComponent } from '../input-selector/input-selector.component';
import { TableMatrixComponent } from './table-matrix.component';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css'],
  imports: [
    MatCard, MatCardHeader, MatCardTitle, MatButton, MatCardContent,
    MatFormField, MatLabel, MatInput, FormsModule, InputSelectorComponent,
    MatHint, DecimalPipe, TableMatrixComponent,
  ],
})
export class TableComponent implements OnChanges {
  inputLabel = '';
  changeStatus = false;
  /** While matrix row/column axis step is open, hide table-level Cancel / Save Table. */
  matrixRowColumnInputStepOpen = false;
  readonly lookupAmountColumnId = 'table-edit-lookup-amount-header';
  readonly rangeEndColumnId = 'table-edit-range-end-header';
  readonly rangeAmountColumnId = 'table-edit-range-amount-header';
  private readonly lookupRowHeaderPrefix = 'table-edit-lookup-row-';
  private readonly rangeRowHeaderPrefix = 'table-edit-range-row-';
  /** Federal SAI floor; used when the table input is raw (possibly negative) SAI. */
  private static readonly EFM_SAI_KEY = 'inarMiscCalculations.efmSAI';
  private static readonly EFM_SAI_RANGE_FLOOR = -1500;

  constructor(
    private ss: SelectorsService,
    private cc: CalcConfigService,
  ) { }

  @Input({ required: true }) table!: Table;
  @Input() editMode = false;
  @Input() isNew = false;
  /** Packaging previews the table without edit or delete. The Tables page keeps both actions. */
  @Input() showActions = true;
  /**
   * One-shot from parent (new-table wizard): matrix editor opens the row/column input step once, then emits
   * `openRowColumnInputStepConsumed` so the parent can clear this flag.
   */
  @Input() openRowColumnInputStepOnce = false;
  @Output() edit = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();
  @Output() saveTable = new EventEmitter<void>();
  @Output() cancelEdit = new EventEmitter<void>();
  @Output() readonly openRowColumnInputStepConsumed = new EventEmitter<void>();

  get isMatrixTable(): boolean {
    return !!this.table?.matrixTable;
  }

  get has1dTableInput(): boolean {
    if (!this.table || this.isMatrixTable) {
      return false;
    }
    if (this.table.type === 'Formula') {
      return !!(this.table.formulaId || this.table.formula);
    }
    return this.table.keyId !== undefined && this.table.keyId !== null && this.table.keyId !== '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editMode']?.currentValue === false) {
      this.matrixRowColumnInputStepOpen = false;
    }
    if (changes['table']) {
      this.matrixRowColumnInputStepOpen = false;
    }
    void changes;
    this.refreshInputLabel();
  }

  onMatrixRowColumnInputEditActive(active: boolean): void {
    this.matrixRowColumnInputStepOpen = active;
  }

  refreshInputLabel(): void {
    if (!this.table) {
      return;
    }
    if (this.isMatrixTable) {
      const mt = this.table.matrixTable;
      if (mt) {
        this.inputLabel = `Matrix — Rows: ${this.axisSummary(mt.rows)}; Columns: ${this.axisSummary(mt.columns)}`;
      }
      return;
    }
    const selector = this.ss.getSelector(this.table.keyId);
    if (this.table.type === 'Formula') {
      this.inputLabel = this.table.formula?.name;
    } else {
      this.inputLabel = selector?.label ?? '';
    }
  }

  /** Display label for a matrix axis (read-only card header). */
  axisSummary(axis: MatrixAxis): string {
    return matrixAxisSummaryLabel(axis, this.axisSummaryContext());
  }

  private axisSummaryContext(): MatrixAxisSummaryContext {
    return {
      selectorLabel: (keyId: string) => this.ss.getSelector(keyId)?.label,
      formulaName: (formulaId: string) => this.cc.getFormula(formulaId)?.name,
    };
  }

  calcRanges(ranges: RangeTest[]) {
    if (!ranges?.length) {
      return;
    }
    // Keep the first band's start (e.g. -1500 for SAI); chain later starts from prior ends.
    let lastFinish = ranges[0].rangeFinish;
    ranges.forEach((range, i) => {
      if (i === 0) return;
      range.rangeStart = this.calcIncrement(lastFinish);
      lastFinish = range.rangeFinish;
    });
  }

  addRow(ranges: RangeTest[]) {
    if (ranges === undefined) {
      const start = this.defaultFirstRangeStart();
      this.table.rangeTests = [{
        rangeStart: start,
        rangeFinish: start,
        result: 0,
        order: 1
      }];
    } else {
      const lastRange = ranges[ranges.length - 1];
      const aRange: RangeTest = {
        rangeStart: this.calcIncrement(lastRange.rangeFinish),
        rangeFinish: this.calcIncrement(lastRange.rangeFinish),
        result: 0,
        order: lastRange.order + 1
      };
      ranges.push(aRange);
    }
  }

  /** First range band floor: -1500 for SAI, otherwise 0. */
  private defaultFirstRangeStart(): number {
    return this.table?.keyId === TableComponent.EFM_SAI_KEY
      ? TableComponent.EFM_SAI_RANGE_FLOOR
      : 0;
  }

  calcIncrement(value: number): number {
    const decimals = ((value % 1) !== 0) ? value.toString().split('.')[1].length : 0;
    const decimalPower = (10.0 ** decimals);

    return Math.round(decimalPower * (value + (1 / decimalPower))) / decimalPower;
  }

  deleteRow(ranges: RangeTest[], index: number) {
    if (ranges.length > 1) {
      ranges.splice(index, 1);
    } else {
      alert('Cannot delete last row');
    }
  }

  changeInput(state: 'edit' | 'save' | 'cancel') {
    switch (state) {
      case 'edit':
        this.changeStatus = true;
        break;
      case 'cancel':
        this.changeStatus = false;
        break;
      case 'save':
        this.changeStatus = false;
        if ((this.table.type === 'Formula' && this.table.formula !== undefined) || this.table.keyId !== undefined) {
          if (this.table.type === 'Formula') {
            this.table.keyId = null;
            this.inputLabel = this.table.formula?.name ?? '';
          } else {
            const selector = this.ss.getSelector(this.table.keyId);
            this.inputLabel = selector?.label ?? '';
          }
          // remove previous table if there
          this.table.lookupTests = [];
          // if a multiple-choice question then build new lookup table
          if (this.table.type === 'Question' && this.table.question.format === 'Code') {
            this.table.rangeTest = false;
            this.table.question.choices.forEach(choice => {
              const aTest: LookupTest = {
                // TODO: Converted tables don't have label
                // label: choice.label,
                lookupValue: choice.value,
                result: 0,
                order: this.table.lookupTests.length
              };
              this.table.lookupTests.push(aTest);
            });

          } else {
            // if it's not a lookup table then it has to be a range table
            this.table.rangeTest = true;
            this.addRow(this.table.rangeTests);
          }
        }
        break;
    }

  }

  getRowHeaderId(tableType: 'lookup' | 'range', index: number): string {
    const prefix = tableType === 'lookup' ? this.lookupRowHeaderPrefix : this.rangeRowHeaderPrefix;
    return `${prefix}${index}`;
  }

}
